import { Action, Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';
import axios from 'axios';
import printJS from 'print-js';
import moment from 'moment';
import store from '@/store';
import { Leaflet } from '@/types/leaflet';
import { Fully } from '@/types/fully';
declare let fully: Fully;

export interface LeafletsStatus {
  interval: number;
  wait: number;
  limit: number;
  current: Leaflet | null;
  url: string;
  isPlaying: boolean;
  getPrevPage: number;
  getNextPage: number;
  pdf: string | null;
}

@Module({ dynamic: true, store, name: 'leaflets', namespaced: true, preserveState: false })
class LeafletsModlue extends VuexModule implements LeafletsStatus {
  /**
   * インターバル間隔（秒）
   *
   * @type {number}
   * @memberof LeafletsModlue
   */
  public interval = 10;

  /**
   * 自動再生復帰までの待機時間
   *
   * @type {number}
   * @memberof LeafletsModlue
   */
  public wait = 0;

  /**
   * 表示制限数
   *
   * @type {number}
   * @memberof LeafletsModlue
   */
  public limit = 0;

  /**
   * チラシごとに読み込んだときにロードを遅らせる時間（ミリ秒）
   *
   * @private
   * @memberof ContentsModlue
   */
  private _lazy = process.env.VUE_APP_LEAFLETS_LOAD_LAZY;

  /**
   * カタログ情報
   *
   * @type {Leaflet[]}
   * @memberof LeafletsModlue
   */
  private _alldatas: Leaflet[] = [];
  private datas: Leaflet[] = [];

  /**
   * 現在表示中のカタログインデックス
   *
   * @type {number}
   * @memberof LeafletsModlue
   */
  private _index = -1;

  /**
   * 現在表示中のカタログのページ数
   *
   * @type {number}
   * @memberof LeafletsModlue
   */
  private _page = 0;

  /**
   * 再生中かどうか
   *
   * @type {boolean}
   * @memberof LeafletsModlue
   */
  private _playing = false;

  /**
   * タイマーID
   *
   * @type {NodeJS.Timeout}
   * @memberof LeafletsModlue
   */
  private _timerId?: NodeJS.Timeout;

  /**
   * 現在表示中の広告データを取得
   *
   * @readonly
   * @type {(Leaflet | null)}
   * @memberof LeafletsModlue
   */
  public get current(): Leaflet | null {
    const datas = this.datas;
    if (datas.length > 0 && this._index !== -1) {
      return datas[this._index];
    }
    return null;
  }

  /**
   * 現在表示中のチラシ画像URLを取得
   *
   * @readonly
   * @type {string}
   * @memberof LeafletsModlue
   */
  public get url(): string {
    let url = '';
    const leaflet = this.current;
    if (leaflet !== null) {
      url = leaflet.items[this._page];
    }
    return url;
  }

  /**
   * 再生中かどうかを取得
   *
   * @readonly
   * @type {boolean}
   * @memberof LeafletsModlue
   */
  public get isPlaying(): boolean {
    return this._playing;
  }

  /**
   * 前のページ番号を取得
   *
   * @readonly
   * @type {number}
   * @memberof LeafletsModlue
   */
  public get getPrevPage(): number {
    let page = 0;
    const leaflet = this.current;
    if (leaflet !== null) {
      page = this._page - 1;
      if (page < 0) {
        page = leaflet.items.length - 1;
      }
    }
    return page;
  }

  /**
   * 次のページ番号を取得
   *
   * @readonly
   * @type {number}
   * @memberof LeafletsModlue
   */
  public get getNextPage(): number {
    let page = 0;
    const leaflet = this.current;
    if (leaflet !== null) {
      page = this._page + 1;
      if (page > leaflet.items.length - 1) {
        page = 0;
      }
    }
    return page;
  }

  /**
   * PDFデータ取得
   *
   * @readonly
   * @type {(string | null)}
   * @memberof LeafletsModlue
   */
  public get pdf(): string | null {
    const leaflet = this.current;
    if (leaflet !== null) {
      return leaflet.pdf || null;
    }
    return null;
  }

  /**
   * インターバル間隔をセット
   *
   * @param {number} value
   * @memberof LeafletsModlue
   */
  @Mutation
  public setInterval(value: number) {
    this.interval = value;
  }

  /**
   * 自動再生復帰までの待機時間をセット
   *
   * @param {number} value
   * @memberof LeafletsModlue
   */
  @Mutation
  public setWait(value: number) {
    this.wait = value;
  }

  /**
   * 表示制限数
   *
   * @param {number} value
   * @memberof LeafletsModlue
   */
  @Mutation
  public setLimit(value: number) {
    this.limit = value;
  }

  /**
   * カタログ情報をセット
   *
   * @param {Leaflet[]} value
   * @memberof LeafletsModlue
   */
  @Mutation
  setDatas(value: Leaflet[]) {
    this._alldatas = value;

    // 現在の日時が指定された日時の範囲内であるカタログ情報のみを抽出
    const now = moment();
    now.set({ second: 0, millisecond: 0 });
    this.datas = this._alldatas.filter(leaflet => {
      // 有効開始日時が現在より後の場合有効
      if (leaflet.enabled_start_at !== null) {
        const startAt = moment(leaflet.enabled_start_at);
        if (startAt.isAfter(now)) {
          return false;
        }
      }

      // 有効終了日時が現在より前の場合非表示
      if (leaflet.enabled_end_at !== null) {
        const endAt = moment(leaflet.enabled_end_at);
        if (endAt.isBefore(now)) {
          return false;
        }
      }

      // 有効日時／年でフィルタリングする
      if (leaflet.enabled_years !== null && leaflet.enabled_years.length > 0) {
        const year = parseInt(now.format('YYYY'), 10);
        if (leaflet.enabled_years.findIndex(value => value === year) === -1) {
          return false;
        }
      }

      // 有効日時／月でフィルタリングする
      if (leaflet.enabled_months !== null && leaflet.enabled_months.length > 0) {
        const month = parseInt(now.format('M'), 10);
        if (leaflet.enabled_months.findIndex(value => value === month) === -1) {
          return false;
        }
      }

      // 有効日時／日でフィルタリングする
      if (leaflet.enabled_days !== null && leaflet.enabled_days.length > 0) {
        // 月末かどうかチェック
        let eom = false;
        if (leaflet.enabled_days.findIndex(value => value === 99) !== -1) {
          const lastDayOfMonth = now.clone().endOf('month');
          eom = now.isSame(lastDayOfMonth, 'day');
        }

        const day = parseInt(now.format('D'), 10);
        if (leaflet.enabled_days.findIndex(value => value === day) === -1 && eom === false) {
          return false;
        }
      }

      // 有効日時／時でフィルタリングする
      if (leaflet.enabled_hours !== null && leaflet.enabled_hours.length > 0) {
        const hour = parseInt(now.format('H'), 10);
        if (leaflet.enabled_hours.findIndex(value => value === hour) === -1) {
          return false;
        }
      }

      // 有効日時／分でフィルタリングする
      if (leaflet.enabled_minutes !== null && leaflet.enabled_minutes.length > 0) {
        const minutes = parseInt(now.format('m'), 10);
        if (leaflet.enabled_minutes.findIndex(value => value === minutes) === -1) {
          return false;
        }
      }

      // 有効日時／週でフィルタリングする
      if (leaflet.enabled_weeks !== null && leaflet.enabled_weeks.length > 0) {
        // 現在の週期を取得
        const lastDayOfWeek = now.clone().endOf('week');
        const firstDayOfMonth = now.clone().startOf('month');
        const weekly = lastDayOfWeek.diff(firstDayOfMonth, 'weeks') + 1;

        // 現在の曜日を取得
        const weekIndex = parseInt(now.format('d'), 10);
        const weekKey = ['U', 'M', 'T', 'W', 'R', 'F', 'S'].find((value, i) => i === weekIndex);

        if (weekKey !== undefined) {
          // 現在の週期と曜日を合わせた文字列を生成して検索をかける
          const weeks = [weekKey + 0, weekKey + weekly];
          const index = leaflet.enabled_weeks.findIndex(value => {
            return weeks.findIndex(week => week === value) !== -1;
          });
          if (index === -1) return false;
        }
      }
      return true;
    });
  }

  /**
   * 現在表示中のカタログインデックスをセット
   *
   * @param {number} value
   * @memberof LeafletsModlue
   */
  @Mutation
  setIndex(value: number) {
    this._index = value;
  }

  /**
   * 現在表示中のカタログのページ数をセット
   *
   * @param {number} value
   * @memberof LeafletsModlue
   */
  @Mutation
  setPage(value: number) {
    this._page = value;
  }

  /**
   * 再生中かどうかをセット
   *
   * @param {boolean} value
   * @memberof LeafletsModlue
   */
  @Mutation
  setPlaying(value: boolean) {
    this._playing = value;
  }

  /**
   * カタログ読み込み開始
   *
   * @returns {Promise<void>}
   * @memberof LeafletsModlue
   */
  @Action({})
  async load(): Promise<void> {
    const config = {
      params: {
        limit: this.limit
      }
    };
    return new Promise((resolve, rejects) => {
      axios
        .get(process.env.VUE_APP_API_DOMAIN + '/api/leaflets', config)
        .then(async response => {
          const leaflets: Leaflet[] = response.data;

          let i = 0;
          const next = () => {
            if (i < leaflets.length) {
              Promise.all(
                leaflets[i].items.map(url => {
                  return this.loadImage(url);
                })
              ).then(async items => {
                leaflets[i].items = items;
                i++;
                const lazyValue = this._lazy ? parseInt(this._lazy, 10) : null;
                if (lazyValue && lazyValue > 0) {
                  await this.lazyload(lazyValue);
                }
                next();
              });
            } else {
              // 全ての画像読み込みが完了したら、データ適応し再生開始
              this.setDatas(leaflets);
              this.setIndex(0);
              this.setPage(0);
              this.play();
              resolve();
            }
          };
          next();
        })
        .catch(rejects);
    });
  }

  /**
   * 画像読み込み
   *
   * @param {string} url
   * @returns {Promise<string>}
   * @memberof LeafletsModlue
   */
  @Action({})
  async loadImage(url: string): Promise<string> {
    return axios.get(url, { responseType: 'arraybuffer' }).then(response => {
      const prefix = `data:${response.headers['content-type']};base64,`;
      return prefix + Buffer.from(response.data, 'binary').toString('base64');
    });
  }

  /**
   * 遅延ロード
   *
   * @param {number} ms
   * @returns {Promise<void>}
   * @memberof ContentsModlue
   */
  @Action({})
  async lazyload(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 読み込んだカタログを削除
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  delete() {
    this._timerId && clearTimeout(this._timerId);
  }

  /**
   * 前のカタログに戻る
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  prev() {
    this.setDatas(this._alldatas);
    // フィルタリングされたチラシが0件だったとき、一定時間処理を停止する
    if (this.datas.length === 0) {
      this._timerId && clearTimeout(this._timerId);
      this._timerId = setTimeout(this.prev, 1000);
      return;
    }

    let index = this._index - 1;
    if (index < 0) {
      index = this.datas.length - 1;
    }
    this.setIndex(index);
    this.setPage(0);
    this.play();
  }

  /**
   * 次のカタログへ進む
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  next() {
    this.setDatas(this._alldatas);
    // フィルタリングされたチラシが0件だったとき、一定時間処理を停止する
    if (this.datas.length === 0) {
      this._timerId && clearTimeout(this._timerId);
      this._timerId = setTimeout(this.next, 1000);
      return;
    }

    let index = this._index + 1;
    if (index > this.datas.length - 1) {
      index = 0;
    }
    this.setIndex(index);
    this.setPage(0);
    this.play();
  }

  /**
   * 前のページ／裏面反転
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  prevPage() {
    this.setPage(this.getPrevPage);
    this.play();
  }

  /**
   * 次のページ／裏面反転
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  nextPage() {
    this.setPage(this.getNextPage);
    this.play();
  }

  /**
   * 自動再生開始
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  play() {
    this._timerId && clearTimeout(this._timerId);

    this.setPlaying(true);
    const interval = (this.current !== null ? this.current.interval : null) || this.interval;
    this._timerId = setTimeout(() => {
      this._timerId && clearTimeout(this._timerId);

      // 全ページ自動表示フラグがONになっているチラシデータの場合、次のページへ進める
      if (this.current !== null && this.current.is_pageauto) {
        if (this.getNextPage > 0) {
          this.nextPage();
        } else {
          // 最後のページの場合は次のチラシへ進める
          this.next();
        }
      } else {
        this.next();
      }
    }, interval * 1000);
  }

  /**
   * 自動再生停止
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  stop() {
    this._timerId && clearTimeout(this._timerId);
    this.setPlaying(false);

    // 待機時間が0以上のときは、待機時間後に自動再生復帰
    if (this.wait > 0) {
      this._timerId = setTimeout(() => {
        this._timerId && clearTimeout(this._timerId);
        this.next();
      }, this.wait);
    }
  }

  /**
   * 印刷
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  print() {
    try {
      // Kioskモードでは全体を印刷
      fully.print();
    } catch (e) {
      const pdf = this.pdf;
      if (pdf !== null) {
        printJS(pdf);
      }
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('fully.print()');
    }
  }
}

export default getModule(LeafletsModlue, store);
